import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert } from 'react-bootstrap';
import PaymentVerificationModal from '../components/modals/PaymentVerificationModal';
import Navbar from '../components/common/Navbar';
import { API_BASE_URL } from '../utils/constants';

const PaymentVerificationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Add this debug log to see if component re-renders

  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pageStatus, setPageStatus] = useState('loading'); // loading, payment_required, success, error, already_verified
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const [country, setCountry] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);

  const randPrices = {
    1: 100, // Basic plan price in Rand
    2: 370  // Plus plan price in Rand
  };

  useEffect(() => {

    // Check URL parameters BEFORE the hash (where Paystack puts them)
    const fullUrlParams = new URLSearchParams(window.location.search);
    const paystackReference = fullUrlParams.get('reference') || fullUrlParams.get('trxref');

    // Check URL parameters AFTER the hash (React Router)
    const hashParams = new URLSearchParams(location.search);
    const status = hashParams.get('status');
    const planId = hashParams.get('planId');

    if (status === 'already_verified') {
      setPageStatus('already_verified');
      setLoading(false);
      return;
    }

    if (status === 'cancelled') {
      setPageStatus('payment_required');
      setError('Payment was cancelled. Please try again.');
      setLoading(false);
      return;
    }

    // Check if this is a Paystack return (has reference parameter in full URL)
    if (paystackReference) {
      // Returning from Paystack
      handlePaystackReturn(paystackReference, planId || sessionStorage.getItem('planId'));
    } else {
      // Initial load - fetch user details
      fetchUserDetails();
    }

  }, [token, location.search, window.location.search]);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        setLocationLoading(true);
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        setCountry(data.country_code);
      } catch (error) {
        console.error('Error detecting location:', error);
        setCountry('UNKNOWN');
      } finally {
        setLocationLoading(false);
      }
    };

    detectLocation();
  }, []);

  const getPriceDisplay = () => {
    if (!userDetails) return '';

    if (country === 'ZA') {
      return `R${randPrices[userDetails.planId]?.toFixed(2) || '0.00'}`;
    } else {
      return `$${parseFloat(userDetails.planPrice).toFixed(2)}`;
    }
  };

  const getCurrencyLabel = () => {
    if (locationLoading) return '';
    if (country === 'ZA') return 'South African Rand';
    return 'US Dollars';
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verification-details/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setUserDetails(data.user);

        if (data.user.verified) {
          setPageStatus('already_verified');
        } else {
          setPageStatus('payment_required');
        }
      } else {
        setError(data.error || 'Failed to fetch verification details');
        setPageStatus('error');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Network error. Please check your connection and try again.');
      setPageStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePaystackReturn = async (reference, planId) => {
    setLoading(true);
    setError(null); // Clear any existing errors

    try {
      // If planId is not provided, try to get it from userDetails or sessionStorage
      const finalPlanId = planId || userDetails?.planId || sessionStorage.getItem('planId');

      if (!finalPlanId) {
        console.error('PaymentVerificationPage - No plan ID found');
        setError('Plan information missing. Please try again.');
        setPageStatus('error');
        setLoading(false);
        return;
      }

      if (!token) {
        console.error('PaymentVerificationPage - No token found');
        setError('Verification token missing. Please try again.');
        setPageStatus('error');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/process-payment-verification/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reference: reference,
          planId: finalPlanId,
          paymentMethod: 'paystack'
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.already_verified) {
          setPageStatus('already_verified');
          setMessage('Your account was already verified.');
        } else {
          setPageStatus('success');
          setMessage('Payment successful! Your account has been verified and activated.');
          setUserDetails(data.user);
        }
      } else {
        console.error('PaymentVerificationPage - Payment verification failed:', data.error);
        setPageStatus('error');
        setError(data.error || 'Payment verification failed');
      }
    } catch (error) {
      console.error('PaymentVerificationPage - Error processing Paystack return:', error);
      setPageStatus('error');
      setError('Network error while processing payment verification. Please check your connection and try again.');
    } finally {
      setLoading(false);

      // Clean up session storage only on success
      if (pageStatus === 'success') {
        sessionStorage.removeItem('paymentRef');
        sessionStorage.removeItem('planId');
        sessionStorage.removeItem('verificationToken');
      }
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    if (paymentData.paymentMethod === 'crypto') {
      // Handle crypto payment success - different flow
      handleCryptoPaymentSuccess(paymentData);
    } else {
      // For PayPal/Paystack payments, process immediately
      processPayPalPayment(paymentData);
    }
  };

  const processPayPalPayment = async (paymentData) => {
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/process-payment-verification/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reference: paymentData.orderID,
          planId: userDetails.planId,
          paymentMethod: 'paypal'
        })
      });

      const data = await response.json();

      if (data.success) {
        setPageStatus('success');
        setMessage('Payment successful! Your account has been verified and activated.');
        setUserDetails(data.user);
        setShowPaymentModal(false);
      } else {
        setError(data.error || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Error processing PayPal payment:', error);
      setError('Network error while processing payment verification.');
    } finally {
      setLoading(false);
    }
  };

  const handleCryptoPaymentSuccess = async (paymentData) => {
    try {
      // Close the payment modal
      setShowPaymentModal(false);

      // Show success message for crypto payment
      setPageStatus('crypto_success');
      setMessage(
        `Cryptocurrency payment submitted successfully! We've received your payment (ID: ${paymentData.paymentId}) and will verify it on the blockchain. You will receive an email confirmation once your payment is verified and your account is activated.`
      );

    } catch (error) {
      console.error('Error processing crypto payment success:', error);
      setError('Payment submitted successfully. Please check your email for confirmation and then try logging in.');
    }
  };

  const handleStartPayment = () => {
    setShowPaymentModal(true);
  };

  const handleRetryPayment = () => {
    // Clear any existing errors and allow retry
    setError(null);
    setPageStatus('payment_required');
    setShowPaymentModal(true);
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (loading || locationLoading) {
    return (
      <>
        <Navbar />
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3">
              {loading ? 'Loading verification details...' : 'Detecting location for pricing...'}
            </p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container className="py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <Card className="shadow-lg">
              <Card.Header className="bg-primary text-white text-center">
                <h3 className="mb-0">Account Verification</h3>
              </Card.Header>
              <Card.Body className="p-4">
                {pageStatus === 'payment_required' && userDetails && (
                  <div className="text-center">
                    <div className="mb-4">
                      <i className="fas fa-credit-card fa-3x text-black mb-3"></i>
                      <h4>Complete Your Registration</h4>
                      <p className="text-black">
                        Welcome, <strong>{userDetails.fullName}</strong>!<br />
                        To activate your account, please complete your payment for the <strong>{userDetails.planName}</strong> plan.
                      </p>
                    </div>

                    <div className="plan-details mb-4">
                      <Card className="text-success">
                        <Card.Body>
                          <h5 className="text-success">{userDetails.planName}</h5>
                          {locationLoading ? (
                            <div className="text-center">
                              <Spinner animation="border" size="sm" variant="success" />
                              <p className="small mt-1">Detecting location for pricing...</p>
                            </div>
                          ) : (
                            <>
                              <h3 className="text-success">{getPriceDisplay()}</h3>
                              <p className="text-black mb-0">
                                Annual Subscription
                                {getCurrencyLabel() && (
                                  <small className="text-muted d-block">
                                    Pricing in {getCurrencyLabel()}
                                    {country === 'ZA' && ' 🇿🇦'}
                                    {country !== 'ZA' && country !== 'UNKNOWN' && ' 🌍'}
                                  </small>
                                )}
                              </p>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </div>

                    {error && (
                      <Alert variant="danger" className="mb-4">
                        {error}
                      </Alert>
                    )}

                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleStartPayment}
                        disabled={loading}
                      >
                        <i className="fas fa-credit-card me-2"></i>
                        Complete Payment
                      </Button>


                      {error && (
                        <Button
                          variant="outline-primary"
                          onClick={handleRetryPayment}
                          disabled={loading}
                        >
                          <i className="fas fa-redo me-2"></i>
                          Try Again
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {pageStatus === 'success' && (
                  <div className="text-center">
                    <div className="mb-4">
                      <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                      <h4 className="text-success">Verification Successful!</h4>
                      <p className="text-black">{message}</p>
                      {userDetails && (
                        <p>Welcome, <strong>{userDetails.fullName}</strong>! Your account is now active.</p>
                      )}
                    </div>

                    <div className="d-grid">
                      <Button variant="success" size="lg" onClick={handleGoToLogin}>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Go to Login
                      </Button>
                    </div>
                  </div>
                )}

                {pageStatus === 'crypto_success' && (
                  <div className="text-center">
                    <div className="mb-4">
                      <i className="fab fa-bitcoin fa-3x text-warning mb-3"></i>
                      <h4 className="text-warning">Cryptocurrency Payment Submitted!</h4>
                      <div className="text-black">
                        <p>{message}</p>
                        <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <small>
                            <i className="fas fa-info-circle mr-2 text-info"></i>
                            <strong>What happens next:</strong>
                            <br />
                            1. We verify your payment on the blockchain (usually 10-20 minutes)
                            <br />
                            2. You'll receive an email confirmation
                            <br />
                            3. Your account will be automatically activated
                            <br />
                            4. You can then log in and access your dashboard
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="d-grid gap-2">
                      <Button variant="primary" size="lg" onClick={handleGoToLogin}>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Go to Login Now
                      </Button>
                      
                    </div>
                  </div>
                )}

                {pageStatus === 'already_verified' && (
                  <div className="text-center">
                    <div className="mb-4">
                      <i className="fas fa-info-circle fa-3x text-info mb-3"></i>
                      <h4 className="text-info">Already Verified</h4>
                      <p className="text-black">
                        Your account has already been verified and is active.
                      </p>
                    </div>

                    <div className="d-grid">
                      <Button variant="primary" size="lg" onClick={handleGoToLogin}>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Go to Login
                      </Button>
                    </div>
                  </div>
                )}

                {pageStatus === 'error' && (
                  <div className="text-center">
                    <div className="mb-4">
                      <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                      <h4 className="text-danger">Verification Error</h4>
                      <p className="text-black">{error || 'An error occurred during verification.'}</p>
                    </div>

                    <div className="d-grid gap-2">
                      <Button variant="outline-primary" onClick={() => window.location.reload()}>
                        <i className="fas fa-redo me-2"></i>
                        Try Again
                      </Button>
                      <Button variant="secondary" onClick={handleGoToLogin}>
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Login
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>

      {/* Payment Modal */}
      {userDetails && (
        <PaymentVerificationModal
          show={showPaymentModal}
          onHide={() => setShowPaymentModal(false)}
          userDetails={userDetails}
          token={token}
          onSuccess={handlePaymentSuccess}
          onError={setError}
        />
      )}
    </>
  );
};

export default PaymentVerificationPage;